type ActionType = 'no_show' | 'cancel' | 'confirm';

interface Props {
  clientName: string;
  actionType?: ActionType;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}

const ACTION_CONFIG: Record<ActionType, {
  title: string;
  description: (name: string) => string;
  confirmLabel: string;
  loadingLabel: string;
  iconClass: string;
  iconBg: string;
  iconColor: string;
  btnClass: string;
  note?: string;
}> = {
  no_show: {
    title: 'Marcar como No llegó',
    description: (name) =>
      `El cliente ${name} no se presentó a su cita. El anticipo pagado quedará registrado como ingreso. Esta acción no se puede deshacer.`,
    confirmLabel: 'Sí, no llegó',
    loadingLabel: 'Guardando...',
    iconClass: 'ri-user-unfollow-line',
    iconBg: 'bg-amber-500/10 border border-amber-500/20',
    iconColor: 'text-amber-400',
    btnClass: 'bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-400 hover:text-amber-300',
  },
  cancel: {
    title: 'Cancelar cita',
    description: (name) =>
      `¿Estás seguro de que quieres cancelar la cita de ${name}? Esta acción no se puede deshacer.`,
    confirmLabel: 'Sí, cancelar',
    loadingLabel: 'Cancelando...',
    iconClass: 'ri-error-warning-line',
    iconBg: 'bg-red-500/10 border border-red-500/20',
    iconColor: 'text-red-400',
    btnClass: 'bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-400 hover:text-red-300',
  },
  confirm: {
    title: 'Confirmar cita manualmente',
    description: (name) =>
      `Vas a confirmar la cita de ${name} de forma manual. Esto la marcará como confirmada aunque el pago en línea no haya sido procesado.`,
    confirmLabel: 'Sí, confirmar',
    loadingLabel: 'Confirmando...',
    iconClass: 'ri-checkbox-circle-line',
    iconBg: 'bg-emerald-500/10 border border-emerald-500/20',
    iconColor: 'text-emerald-400',
    btnClass: 'bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-400 hover:text-emerald-300',
    note: 'Úsalo cuando el cliente pagó en efectivo o por transferencia directa y el sistema no lo detectó automáticamente.',
  },
};

export default function ConfirmCancelModal({ clientName, actionType = 'cancel', onConfirm, onCancel, loading }: Props) {
  const cfg = ACTION_CONFIG[actionType];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-[#141414] border border-white/10 rounded-2xl p-6 w-full max-w-sm">
        {/* Icon */}
        <div className="flex items-center justify-center mb-4">
          <div className={`w-12 h-12 flex items-center justify-center rounded-full ${cfg.iconBg}`}>
            <i className={`${cfg.iconClass} ${cfg.iconColor} text-xl`} />
          </div>
        </div>

        {/* Text */}
        <h3
          className="text-white text-base font-semibold text-center mb-2"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {cfg.title}
        </h3>
        <p className="text-white/45 text-sm text-center leading-relaxed mb-6">
          {cfg.description(clientName)}
        </p>

        {/* No-show financial note */}
        {actionType === 'no_show' && (
          <div className="flex items-start gap-2.5 bg-amber-500/6 border border-amber-500/15 rounded-xl px-4 py-3 mb-5">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-money-dollar-circle-line text-amber-400/70 text-sm" />
            </div>
            <p className="text-amber-400/70 text-xs leading-relaxed">
              El anticipo pagado <strong className="text-amber-400/90">no será reembolsado</strong> y quedará registrado como ingreso del negocio.
            </p>
          </div>
        )}

        {/* Confirm manual note */}
        {actionType === 'confirm' && cfg.note && (
          <div className="flex items-start gap-2.5 bg-emerald-500/6 border border-emerald-500/15 rounded-xl px-4 py-3 mb-5">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-emerald-400/70 text-sm" />
            </div>
            <p className="text-emerald-400/70 text-xs leading-relaxed">{cfg.note}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white text-sm font-medium py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            Volver
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 ${cfg.btnClass} text-sm font-medium py-2.5 rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2`}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-loader-4-line text-sm animate-spin" />
                </div>
                {cfg.loadingLabel}
              </>
            ) : (
              cfg.confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
